import { SocketIo } from "App";
import { ColorType, createChart } from "lightweight-charts";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { formatTimestamp, isWithinOneMinute } from "utils";
import { getEquityData } from "../../../Redux/ChallengeOverview/ChallengeOverviewAction";
import noData from "../../../Assets/Images/noData.svg";
import { useTheme } from "hooks/ThemeContext";

const TotatlPl = ({ currentSeries, setCurrentSeries }) => {
  const { theme } = useTheme();
  const filters = ["1H", "1D", "1W", "All"];
  const tooltipRef = useRef(null);
  const dispatch = useDispatch();

  const [pl, setPl] = useState();

  const chartRef = useRef(null);
  const areaSeriesRef = useRef(null);
  const backgroundColor = "transparent";
  const lineColor = theme === "dark" ? "white" : "black";
  const textColor = theme === "dark" ? "black" : "white";
  const areaTopColor = "#1870B2";
  const areaBottomColor = "rgba(12, 53, 88, 0)";
  const chartContainerRef = useRef(null);
  const [dataSets, setDataSets] = useState([]);
  const {
    equityData,
    equityDataLoading,
    challengeStageInfo,
    challengeStageInfoLoading,
  } = useSelector((state) => state.ChallengeOverviewReducer);

  useEffect(() => {
    if (equityData?.length > 0) {
      setPl(equityData[equityData?.length - 1]?.PL);
      setDataSets(
        equityData.map((data) => ({
          time: new Date(data.createdAt).getTime() / 1000,
          value: data.PL,
        }))
      );
    } else {
      setDataSets([]);
    }
  }, [equityData]);

  useEffect(() => {
    // Create the chart
    if (!chartRef.current) {
      chartRef.current = createChart(chartContainerRef.current, {
        width: chartContainerRef.current.clientWidth,
        height: 250,
        layout: {
          background: { type: ColorType.Solid, color: backgroundColor },
          textColor: textColor,
        },

        handleScroll: {
          mouseWheel: false,
        },
        handleScale: {
          mouseWheel: false,
        },

        grid: {
          vertLines: { visible: false }, // Hide vertical grid lines
          horzLines: { visible: false }, // Hide horizontal grid lines
        },
        timeScale: {
          borderVisible: false,
          secondsVisible: true,
          timeVisible: true,
        },
        rightPriceScale: {
          borderVisible: false,
        },
      });

      areaSeriesRef.current = chartRef.current.addLineSeries({
        color: lineColor,
        lineWidth: 1,
        lastPriceAnimation: "OnDataUpdate",
      });
    }
    areaSeriesRef.current.setData(dataSets);

    const toolTipWidth = 300;
    const toolTipHeight = 80;
    const tooltip = tooltipRef.current;
    const toolTipMargin = 15;

    // Create and style the tooltip html element
    tooltip.style = `width: ${toolTipWidth}px; height: 80px; position: absolute; display: none; padding: 15px; padding-top:8px ; padding-bottom:8px  box-sizing: border-box; font-size: 12px; text-align: left; z-index: 1000; top: 12px; left: 12px; pointer-events: none; border-radius:10px; border-bottom: none; box-shadow: 0 2px 5px 0 rgba(33, 35, 36, 0.45);font-family: N-Roman; -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale;`;
    tooltip.style.background = `rgba(${"31, 31, 31"}, 1`;
    tooltip.style.color = "var(--white)";
    tooltip.style.borderColor = "rgba( 239, 83, 80, 1)";
    chartRef.current.subscribeCrosshairMove((param) => {
      if (
        param.point === undefined ||
        !param.time ||
        param.point.x < 0 ||
        param.point.x > chartContainerRef.current.clientWidth ||
        param.point.y < 0 ||
        param.point.y > chartContainerRef.current.clientHeight
      ) {
        tooltip.style.display = "none";
        return;
      }

      const price = param.seriesData
        .get(areaSeriesRef.current)
        ?.value.toFixed(2);
      const date = param.time;

      if (price) {
        tooltip.style.display = "block";
        tooltip.innerHTML = `<div style="display: flex; width: 100%; justify-content: space-between; flex-direction: row " ><div style="color: ${"rgba( 255, 255, 255, 0.5)"}">P&L</div>
          <div style="color: ${"rgba( 255, 255, 255, 0.5)"}">${formatTimestamp(
          date
        )}</div>
          </div>
          </div><div style="font-size: 24px; margin: 4px 0px; font-family: N-Bold; color: ${
            price > 0 ? "var(--green)" : "var(--red)"
          }">
         ${parseFloat(price).toLocaleString("en-US", {
           style: "currency",
           currency: "USD",
         })}
            </div>`;

        const y = param.point.y;
        let left = param.point.x + toolTipMargin;
        if (left > chartRef.current.clientWidth - toolTipWidth) {
          left = param.point.x - toolTipMargin - toolTipWidth;
        }

        let top = y + toolTipMargin;
        if (top > chartRef.current.clientHeight - toolTipHeight) {
          top = y - toolTipHeight - toolTipMargin;
        }
        if (param.point.x + 300 > chartContainerRef.current.clientWidth) {
          tooltip.style.left = left - 300 + "px";
        } else {
          tooltip.style.left = left + "px";
        }
        tooltip.style.top = top + "px";
      }
    });

    // Set data for the selected series

    return () => {
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.timeScale().fitContent();
      areaSeriesRef.current.setData(dataSets);
    }
  }, [dataSets]);
  useEffect(() => {
    if (challengeStageInfo?.Account?.login && dataSets?.length > 0) {
      const timestamp = dataSets[dataSets?.length - 1]?.time;
      const date = new Date(timestamp * 1000);
      const isoString = date.toISOString();
      SocketIo.on(challengeStageInfo?.Account?.login, (data) => {
        setPl(data.PL);
        if (isWithinOneMinute(data.createdAt, isoString)) {
          const d = [...dataSets];
          d.push({
            time: new Date(data.createdAt).getTime() / 1000,
            value: data.PL,
          });
          setDataSets(d);
        } else {
          const d = [...dataSets];

          d[d?.length - 1] = {
            ...d[d?.length - 1],
            value: data.PL,
          };
          setDataSets(d);
        }
      });
    }
    return () => {
      SocketIo.off(challengeStageInfo?.Account?.login);
    };
  }, [challengeStageInfo, dataSets]);

  return (
    <div className="chart_cont bg-background border-2 border-border-darkNew  rounded-lg h-full justify-end flex flex-col relative p-4  xl:min-w-[30%]  w-full">
      <div className="flex justify-between border-b-2 border-b-border-darkNew pb-4 mb-4">
        <div className="text-base">Floating P&L</div>
        <div className="text-lg font-N_Bold">
          {pl ? pl?.toLocaleString() : "0"}
        </div>
      </div>
      <div className="flex flex-col justify-between h-full relative">
        {equityData?.length > 0 && equityData && (
          <div className="flex justify-between gap-2 bg-black-dark p-2  rounded-xl w-fit absolute z-10 top-0 left-0">
            {filters.map((filter) => (
              <span
                className="text-white-opacity  text-sm font-N_Roman cursor-pointer hover:opacity-100 px-4   rounded-md"
                onClick={() => setCurrentSeries(filter)}
                style={{
                  backgroundColor:
                    currentSeries == filter && "var(--foreground)",
                  color: currentSeries == filter && "var(--primary)",
                }}
              >
                {filter}
              </span>
            ))}
          </div>
        )}

        <div
          className="mt-10"
          ref={chartContainerRef}
          style={{
            position: "relative",
            width: "100%",
            height: "250px",
          }}
        >
          <div ref={tooltipRef} className="custom-tooltip absolute" />
        </div>
        {(!equityData?.length || equityData?.length == 0) && (
          <div className="text-base text-white-opacity h-full flex flex-col gap-0 items-center justify-center cursor-pointer absolute z-10 w-full">
            <div className="font-N_Medium text-2xl ">No Data Found!</div>
            <div className="font-N_Light text-base w-full max-w-[18rem]  text-center">
              You haven't participated in any trade yet.
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TotatlPl;
